import React, { useEffect, useState } from "react";
import { WifiOff, CloudUpload, CheckCircle2, RefreshCw } from "lucide-react";
import { countQueue } from "@/lib/offlineQueue";
import { subscribeOffline, syncNow } from "@/lib/api";

export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [queued, setQueued] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [flash, setFlash] = useState(null); // {kind, text}

  useEffect(() => {
    const refreshQueue = async () => {
      try {
        setQueued(await countQueue());
      } catch {
        setQueued(0);
      }
    };
    refreshQueue();

    const onEvt = (evt) => {
      if (evt.type === "online") {
        setOnline(true);
      } else if (evt.type === "offline") {
        setOnline(false);
      } else if (evt.type === "queued") {
        refreshQueue();
      } else if (evt.type === "sync-start") {
        setSyncing(true);
      } else if (evt.type === "sync-end") {
        setSyncing(false);
        refreshQueue();
        if (evt.replayed > 0) {
          setFlash({
            kind: "success",
            text: `${evt.replayed} action(s) synchronisée(s)`,
          });
          setTimeout(() => setFlash(null), 4000);
        }
        if (evt.failed > 0) {
          setFlash({
            kind: "error",
            text: `${evt.failed} action(s) en échec — voir console`,
          });
          setTimeout(() => setFlash(null), 6000);
        }
      }
    };

    const unsub = subscribeOffline(onEvt);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      unsub();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const handleSync = async () => {
    if (syncing || !online) return;
    await syncNow();
  };

  if (online && queued === 0 && !flash) return null;

  if (!online) {
    return (
      <div
        className="w-full bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center justify-center gap-2"
        data-testid="offline-banner"
      >
        <WifiOff className="w-4 h-4" />
        <span className="font-medium">Mode hors ligne</span>
        <span className="text-amber-700">
          — vos modifications seront synchronisées au retour du réseau
          {queued > 0 && (
            <span className="ml-1 font-semibold">({queued} en attente)</span>
          )}
        </span>
      </div>
    );
  }

  if (queued > 0) {
    return (
      <div
        className="w-full bg-blue-50 border-b border-blue-200 text-blue-900 text-sm px-4 py-2 flex items-center justify-center gap-3"
        data-testid="offline-banner"
      >
        <CloudUpload className="w-4 h-4" />
        <span className="font-medium">
          {queued} action(s) hors ligne à synchroniser
        </span>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-1 rounded-full bg-blue-900 text-white px-3 py-1 text-xs hover:bg-blue-800 disabled:opacity-60"
          data-testid="offline-sync-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Synchronisation..." : "Synchroniser maintenant"}
        </button>
      </div>
    );
  }

  if (flash) {
    const isOk = flash.kind === "success";
    return (
      <div
        className={`w-full text-sm px-4 py-2 flex items-center justify-center gap-2 border-b ${
          isOk
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-red-50 border-red-200 text-red-900"
        }`}
        data-testid="offline-banner"
      >
        <CheckCircle2 className="w-4 h-4" />
        <span className="font-medium">{flash.text}</span>
      </div>
    );
  }

  return null;
}
